import { BeachLoader } from "@/components/ui/BeachLoader";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { supabase } from "@/integrations/supabase/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { TenantsProvider } from "@/hooks/useTenants";
import { TenantProvider } from "@/hooks/useTenant";
import Navbar from "@/components/Navbar";
import MobileBottomNav from "@/components/MobileBottomNav";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import { initTracking, setTrackingUser, trackPageView } from "@/lib/track";
import { loadVisitorContext } from "@/lib/visitorContext";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { notePage } from "@/lib/sessionTrail";
import PhoneCountryConfirmDialog from "@/components/PhoneCountryConfirmDialog";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { lazy, Suspense, useEffect, useState } from "react";
import Index from "./pages/Index";
import ProtectedRoute from "./components/ProtectedRoute";
import CommandPalette from "./components/CommandPalette";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
// Note: GoogleMapsProvider previously mounted <APIProvider> at the app
// root, which eagerly loaded the Google Maps JS even on pages without a
// map (PSI flagged ~15s on the critical path). APIProvider is now
// inlined inside PropertyMap and MapLocationPicker so the script only
// loads when a map actually mounts.

// Lazy-load every non-landing route so first-load JS only contains what the
// homepage actually needs. Each page becomes its own chunk fetched on demand.
const BrowseProperties = lazy(() => import("./pages/BrowseProperties"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const About = lazy(() => import("./pages/About"));
const ListPropertyNew = lazy(() => import("./pages/ListPropertyNew"));
const ListPropertySuccess = lazy(() => import("./pages/ListPropertySuccess"));
const OwnerOnboarding = lazy(() => import("./pages/OwnerOnboarding"));
const AreaPage = lazy(() => import("./pages/AreaPage"));
const Collections = lazy(() => import("./pages/Collections"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const ExploreAreas = lazy(() => import("./pages/ExploreAreas"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Profile = lazy(() => import("./pages/Profile"));

// On an /admin URL, start downloading the admin while sign-in and the
// profile check run, instead of after them.
const loadAdmin = () => import("./pages/AdminDashboard");
const loadAdminLayout = () => import("./components/admin/AdminLayout");
if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) { void loadAdmin(); void loadAdminLayout(); }
const AdminDashboard = lazy(loadAdmin);
// The investor deck: its own full-screen page, no site chrome (HANDOFF.md §9.15).
const DeckPage = lazy(() => import("./pages/deck/DeckPage"));
const DeckPreviewPage = lazy(() => import("./pages/deck/DeckPage").then((m) => ({ default: m.DeckPreviewPage })));
const DeckPrintPage = lazy(() => import("./pages/deck/DeckPrintPage"));
const AdminLayout = lazy(loadAdminLayout);
const BugReporter = lazy(() => import("./components/admin/BugReporter"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
// Dev-only gallery of every overlay for the phone audit (never in the production bundle).
const UiAudit = import.meta.env.DEV ? lazy(() => import("./dev/UiAudit")) : null;
const Welcome = lazy(() => import("./pages/Welcome"));
const OwnerDashboard = lazy(() => import("./pages/OwnerDashboard"));
const UnifiedMessages = lazy(() => import("./pages/UnifiedMessages"));
const EditProperty = lazy(() => import("./pages/EditProperty"));
const ClaimListing = lazy(() => import("./pages/ClaimListing"));
const MsthLaunchPoster = lazy(() => import("./pages/admin/posters/MsthLaunchPoster"));
const ListingSharePoster = lazy(() => import("./pages/admin/posters/ListingSharePoster"));
const ProductBacklog = lazy(() => import("./pages/admin/ProductBacklog"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Badges = lazy(() => import("./pages/Badges"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Verify = lazy(() => import("./pages/Verify"));
const PurchaseThanks = lazy(() => import("./pages/PurchaseThanks"));
const Cookies = lazy(() => import("./pages/Cookies"));
const PrivacyRequest = lazy(() => import("./pages/PrivacyRequest"));
const Brand = lazy(() => import("./pages/Brand"));
const EmbedListings = lazy(() => import("./pages/EmbedListings"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const StillLooking = lazy(() => import("./pages/StillLooking"));
const NotFound = lazy(() => import("./pages/NotFound"));

const HIDE_FOOTER_ROUTES = ["/about"];
// Chat screens are app screens on phones (Oct 17): the footer under the
// Messages box made the page scroll, and iOS left it shifted after the keyboard.
const PHONE_NO_FOOTER_ROUTES = ["/messages", "/inbox"];

// Routes that render bare, no Navbar, Footer, or mobile bottom nav. Used by
// the partner iframe embed so the host page's chrome is the only chrome.
// /area/<slug> (singular) was the canonical path once; every link, the
// sitemap and the JSON-LD now use /areas/<slug>. Keep old links working.
const LegacyAreaRedirect = () => {
  const { area } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/areas/${area ?? ""}${search}`} replace />;
};

const isEmbedPath = (pathname: string) => pathname.startsWith("/embed");

// Admin routes have their own chrome (AdminLayout). The consumer Navbar /
// MobileBottomNav are suppressed so they don't overlay the admin shell.
const isAdminPath = (pathname: string) => pathname.startsWith("/admin");
const isDeckPath = (pathname: string) => pathname === "/deck" || pathname.startsWith("/deck/");

const RouteFallback = () => <BeachLoader className="min-h-[60vh]" />;

// 🐛 quick bug report, only for admins (the code loads only for them).
const AdminBugButton = () => {
  const { data: isAdmin } = useIsAdmin();
  if (!isAdmin) return null;
  return <Suspense fallback={null}><BugReporter /></Suspense>;
};

const AppLayout = () => {
  const location = useLocation();
  const embedMode = isEmbedPath(location.pathname);
  const adminMode = isAdminPath(location.pathname);
  const deckMode = isDeckPath(location.pathname);
  const bareChrome = embedMode || adminMode || deckMode;
  const hideFooter = bareChrome || HIDE_FOOTER_ROUTES.includes(location.pathname);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const tenant = useTenant();
  const { user } = useAuth();

  // First-party analytics: one session per tab, a page_view per route change,
  // the signed-in user attached server-side from the JWT (see src/lib/track.ts).
  useEffect(() => {
    initTracking({ tenantId: tenant.id });
    // Where the visitor is (our edge's view, no IP): analytics country and prices.
    void loadVisitorContext();
  }, [tenant.id]);
  useEffect(() => {
    setTrackingUser(user?.id ?? null);
    // A new account remembers the market it signed up on (Oct 17). Email
    // sign-ups send it with the sign-up; Google can't, so the first sign-in
    // claims it. The database keeps the first answer and ignores accounts
    // older than an hour, so calling it on every sign-in is harmless.
    if (user?.id) void supabase.rpc("claim_signup_tenant", { p_tenant: tenant.id });
  }, [user?.id, tenant.id]);
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  useEffect(() => {
    notePage(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Global ⌘K / Ctrl+K toggles the command palette. Suppressed inside
  // partner embeds, the host page owns the keyboard there. The Navbar's
  // ⌘K button dispatches `msth:open-command-palette` to toggle without
  // needing to plumb state through context.
  useEffect(() => {
    if (embedMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    const onCustom = () => setPaletteOpen((v) => !v);
    window.addEventListener("keydown", onKey);
    window.addEventListener("msth:open-command-palette", onCustom);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("msth:open-command-palette", onCustom);
    };
  }, [embedMode]);

  return (
    <div className={bareChrome ? "" : "pb-[var(--app-bottom-nav-reserve)]"}>
      {!bareChrome && <MaintenanceBanner />}
      {!bareChrome && <Navbar />}
      <main>
      <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/properties" element={<BrowseProperties />} />
          <Route path="/properties/:slug" element={<PropertyDetail />} />
          <Route path="/areas" element={<ExploreAreas />} />
          <Route path="/areas/:area" element={<AreaPage />} />
          <Route path="/area/:area" element={<LegacyAreaRedirect />} />
          {/* Collections: intent pages ("pet-friendly monthly rentals"), city-wide or per area. */}
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:slug" element={<CollectionPage />} />
          <Route path="/collections/:slug/:area" element={<CollectionPage />} />
          <Route path="/about" element={<About />} />
          {/* Old simplified /list-property form was removed, owner-onboarding is the canonical entry point. */}
          <Route path="/list-property" element={<OwnerOnboarding />} />
          <Route path="/list-property/new" element={<ListPropertyNew />} />
          <Route path="/list-property/success" element={<ListPropertySuccess />} />
          <Route path="/owner-onboarding" element={<OwnerOnboarding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          {/* Consolidated into the owner dashboard; keep the path as a redirect
              so old links/bookmarks still land somewhere sensible. */}
          <Route path="/my-properties" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute><OwnerDashboard /></ProtectedRoute>} />
          {/* One conversations surface, /inbox lives on as a redirect for old
              links and email deep-links. */}
          <Route path="/inbox" element={<Navigate to="/messages" replace />} />
          <Route path="/messages" element={<ProtectedRoute><UnifiedMessages /></ProtectedRoute>} />
          <Route path="/edit-property/:id" element={<ProtectedRoute><EditProperty /></ProtectedRoute>} />
          
          <Route path="/claim-listing" element={<ClaimListing />} />

          {/* All admin routes share the AdminLayout shell (top bar + mobile
              bottom nav). Consumer Navbar / MobileBottomNav are suppressed
              for these paths by the isAdminPath check above. */}
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/posters/msth-launch" element={<ProtectedRoute><MsthLaunchPoster /></ProtectedRoute>} />
            <Route path="/admin/posters/listing-share" element={<ProtectedRoute><ListingSharePoster /></ProtectedRoute>} />
            <Route path="/admin/product-backlog" element={<ProtectedRoute><ProductBacklog /></ProtectedRoute>} />
          </Route>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/badges" element={<Badges />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/verify" element={<ProtectedRoute><Verify /></ProtectedRoute>} />
          <Route path="/thanks" element={<ProtectedRoute><PurchaseThanks /></ProtectedRoute>} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/privacy-request" element={<PrivacyRequest />} />
          <Route path="/brand" element={<Brand />} />
          <Route path="/deck" element={<DeckPage />} />
          <Route path="/deck/preview/:investorId" element={<DeckPreviewPage />} />
          <Route path="/deck/print" element={<DeckPrintPage />} />
          <Route path="/embed/listings" element={<EmbedListings />} />
          <Route path="/still-looking" element={<StillLooking />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          {UiAudit && <Route path="/__ui" element={<UiAudit />} />}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
      </main>
      {!hideFooter && (PHONE_NO_FOOTER_ROUTES.includes(location.pathname) ? <div className="hidden md:block"><Footer /></div> : <Footer />)}
      {!bareChrome && <MobileBottomNav />}
      {!bareChrome && <CookieConsent />}
      {!bareChrome && <PhoneCountryConfirmDialog />}
      {!embedMode && !deckMode && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}
      {!embedMode && !deckMode && <AdminBugButton />}
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TenantsProvider>
        <TenantProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <MaintenanceGate>
              <AppLayout />
            </MaintenanceGate>
          </BrowserRouter>
        </TooltipProvider>
        </TenantProvider>
        </TenantsProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
